import { useRef } from 'react';
import { useEffect, useState } from 'react';
import { checkWordCount } from '../tpc/countWords';

interface WordCountTextareaProps {
    id: string,
    min: number,
    max: number,
    value: string,
    onChange: (e: Event) => any,

    className?: string,
    maxLines?: number,
    name?: string,
    // User-facing description of the rules for this textarea. Overwritten once they start typing.
    label?: string,
}


/**
 * A textarea that makes the user type at least `min` and at most `max` words using the same logic/behavior we use in
 * TPC.
 *
 * Note on implementation: the TPC word count behavior relies on direct DOM manipulation, which doesn't play nicely with
 * React. The normal way to manage something like this is to have the component render something completely static (so
 * React never tries to update it), then move all DOM manipulation into useEffect.
 */
export const WordCountTextarea = ({ id, min, max, maxLines, className, value, onChange, name, label, }: WordCountTextareaProps) => {
    const refTextarea = useRef<HTMLTextAreaElement>(null);
    const refLabel = useRef<HTMLDivElement>(null);
    const [madeChanges, setMadeChanges] = useState(false);

    // One big useEffect syncing DOM updates to props we don't expect to change frequently. This is somewhat inefficient
    // (we'll re-set ID every time you change `max`, for example), but it's less complicated than having a separate
    // useEffect for every prop.
    useEffect(() => {
        // Props that modify the textarea
        if (refTextarea.current) {
            refTextarea.current.id = id;
            refTextarea.current.dataset.min = min.toString();
            refTextarea.current.dataset.max = max.toString();
            refTextarea.current.dataset.maxLines = maxLines?.toString();
            if (className) {
                refTextarea.current.className = className;
            }
            if (name) {
                refTextarea.current.name = name;
            }
        }
        // Props that modify the little "words remaining" label div
        if (refLabel.current) {
            refLabel.current.id = `${id}-remaining`;
        }
    }, [id, min, max, maxLines, className, name, refTextarea.current, refLabel.current]);

    useEffect(() => {
        if (refTextarea.current) {
            const curr = refTextarea.current;
            curr.addEventListener('keyup', countWords)
            return () => curr.removeEventListener('keyup', countWords);
        }
    }, [refTextarea.current]);

    useEffect(() => {
        if (refTextarea.current) {
            refTextarea.current.value = value;
        }
    }, [value, refTextarea]);

    useEffect(() => {
        if (refTextarea.current) {
            const curr = refTextarea.current;
            const fullChangeHandler = (e: Event) => {
                setMadeChanges(true);
                onChange(e);
            }
            curr.addEventListener('change', fullChangeHandler);

            return () => {
                curr.removeEventListener('change', fullChangeHandler);
            }
        }
    }, [onChange, refTextarea.current])

    // Remove label once editing starts
    useEffect(() => {
        if (!madeChanges && refLabel.current) {
            refLabel.current.className = 'character-count smallprint';
            refLabel.current.textContent = label ?? null;
        }
    }, [madeChanges, refLabel.current]);

    function countWords() {
        checkWordCount(refTextarea.current);
    }

    return (
            <div>
                <textarea ref={refTextarea} />
                <div ref={refLabel} />
            </div>
    );
}
